import { Alert, Box, Button, Chip } from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  dismissToast,
  selectQueuedCount,
  selectVisibleToasts,
  type Toast,
} from '@/features/toasts/slice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  AUTO_DISMISS_DURATION,
  COLLAPSE_DURATION,
  ITEM_GAP,
  STAGGER_MS,
  slideInAnimation,
  slideOutAnimation,
  ToastGlobalStyles,
  timerAnimation,
} from './toastAnimations'

const ToastItem = memo(function ToastItem({
  toast,
  enterDelay = 0,
}: {
  toast: Toast
  enterDelay?: number
}) {
  const dispatch = useAppDispatch()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dismissing, setDismissing] = useState(false)
  const [collapsing, setCollapsing] = useState(false)

  const startDismiss = useCallback(() => setDismissing(true), [])

  /** Called when the slide-out CSS animation finishes — begin grid row collapse. */
  const handleSlideEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === 'toast-slide-out') setCollapsing(true)
  }, [])

  /** Called when the grid-template-rows transition finishes — remove from store. */
  const handleCollapseEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName === 'grid-template-rows')
        dispatch(dismissToast(toast.id))
    },
    [dispatch, toast.id],
  )

  useEffect(() => {
    if (toast.dismissMode === 'auto') {
      timerRef.current = setTimeout(startDismiss, AUTO_DISMISS_DURATION)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [toast.dismissMode, startDismiss])

  const needsInteraction =
    toast.dismissMode === 'requires-interaction' ||
    toast.dismissMode === 'search-dismisses'

  return (
    // Outer grid wrapper: animates grid-template-rows 1fr→0fr to collapse without measuring height.
    <div
      onTransitionEnd={collapsing ? handleCollapseEnd : undefined}
      style={{
        display: 'grid',
        gridTemplateRows: collapsing ? '0fr' : '1fr',
        marginBottom: collapsing ? 0 : ITEM_GAP,
        transition: collapsing
          ? `grid-template-rows ${COLLAPSE_DURATION}ms ease, margin-bottom ${COLLAPSE_DURATION}ms ease`
          : 'none',
      }}
    >
      {/* Inner div must have min-height:0 so the row can shrink below its content height. */}
      <div style={{ minHeight: 0, overflow: 'hidden' }}>
        <Box
          onAnimationEnd={dismissing ? handleSlideEnd : undefined}
          sx={{
            position: 'relative',
            borderRadius: '4px',
            overflow: 'hidden',
            boxShadow: 3,
            animation: dismissing
              ? slideOutAnimation
              : slideInAnimation(enterDelay),
          }}
        >
          <Alert
            variant="filled"
            severity={toast.severity}
            action={
              needsInteraction ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={startDismiss}
                  aria-label="Dismiss notification"
                  sx={{
                    color: 'inherit',
                    borderColor: 'currentColor',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderColor: 'currentColor',
                    },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Dismiss
                </Button>
              ) : undefined
            }
            onClose={needsInteraction ? undefined : startDismiss}
            sx={{ width: '100%', boxShadow: 0, borderRadius: 0 }}
          >
            {toast.message}
            {needsInteraction && (
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                }}
              >
                {' '}
                This notification requires manual dismissal.
              </Box>
            )}
          </Alert>

          {/* Status bar — decorative */}
          <Box
            aria-hidden="true"
            sx={{
              height: '5px',
              width: '100%',
              ...(needsInteraction
                ? {
                    background: (theme) =>
                      `repeating-linear-gradient(60deg, ${theme.palette[toast.severity].main} 0px, ${theme.palette[toast.severity].main} 5px, ${theme.palette[toast.severity].dark} 5px, ${theme.palette[toast.severity].dark} 12px)`,
                  }
                : {
                    bgcolor: (theme) => theme.palette[toast.severity].dark,
                    '&::after': {
                      content: '""',
                      display: 'block',
                      height: '100%',
                      width: '100%',
                      bgcolor: (theme) => theme.palette[toast.severity].main,
                      transformOrigin: 'left center',
                      animation: timerAnimation,
                    },
                  }),
            }}
          />
        </Box>
      </div>
    </div>
  )
})

export default function ToastSnackbar() {
  const visibleToasts = useAppSelector(selectVisibleToasts)
  const queuedCount = useAppSelector(selectQueuedCount)
  const prevCountRef = useRef(queuedCount)
  const [shakeKey, setShakeKey] = useState(0)

  // Track which IDs have already played their enter animation.
  const seenIdsRef = useRef<Set<string>>(new Set())
  // Compute stagger delays for IDs appearing for the first time this render.
  const staggerDelays = new Map<string, number>()
  let newIndex = 0
  for (const toast of visibleToasts) {
    if (!seenIdsRef.current.has(toast.id)) {
      staggerDelays.set(toast.id, newIndex * STAGGER_MS)
      newIndex++
    }
  }

  // After each render, mark all currently visible IDs as seen and
  // prune stale IDs so the set doesn't grow unbounded.
  useEffect(() => {
    const currentIds = new Set(visibleToasts.map((t) => t.id))
    for (const toast of visibleToasts) {
      seenIdsRef.current.add(toast.id)
    }
    for (const id of Array.from(seenIdsRef.current)) {
      if (!currentIds.has(id)) {
        seenIdsRef.current.delete(id)
      }
    }
  }, [visibleToasts])

  useEffect(() => {
    if (queuedCount > prevCountRef.current) {
      setShakeKey((k) => k + 1)
    }
    prevCountRef.current = queuedCount
  }, [queuedCount])

  if (visibleToasts.length === 0) return null

  return (
    <>
      {ToastGlobalStyles}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: 400,
          width: '100%',
        }}
      >
        {visibleToasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            enterDelay={staggerDelays.get(toast.id) ?? 0}
          />
        ))}
        {queuedCount > 0 && (
          <Chip
            key={shakeKey}
            label={`+${queuedCount} more`}
            size="small"
            variant="filled"
            aria-live="polite"
            aria-atomic="true"
            sx={{
              alignSelf: 'flex-end',
              animation: shakeKey > 0 ? 'chip-shake 0.4s ease' : undefined,
            }}
          />
        )}
      </Box>
    </>
  )
}
